import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { updateBrokerConnectData } from '@actions/brokerConnect/brokerConnectActionCreators';
import { configs } from '@constants/configs/configs';

//components
import InputPhoneNumber from './components/InputPhoneNumber';
import Alert from './Alert';
import ErrorMessage from './components/ErrorMessage';
import { ConditionalRendering } from '@components/common/conditional-rendering';

import { ACTIVITY_TYPES } from '@constants/common/statusTypes';

const Participation = props => {

    const { brokerInfo, actions, environment: { stateCode, isState, exchangeName } } = props;
    const isStateVA = isState.VA || isState.GI;
    const { status, phoneNumber, tempPhoneNumber=null } = brokerInfo;
    const { endpoints, routes } = configs;

    const navigate = useNavigate();

    const [ isChecked, setChecked ] = useState(false);
    const [ showErrorMessage, setErrorMessage ] = useState(false);
    const [ showSuccessMessage, setSuccessMessage ] = useState(false);
    const [ showPhoneErrorMessage, setPhoneErrorMessage ] = useState(false);
    const [ currentStatus, setCurrentStatus ] = useState(status);
    /** Suppresses the "currently enrolled" layout while enrolling so Redux (ACTIVE) does not flash before navigation. */
    const [ isEnrollingTransition, setIsEnrollingTransition ] = useState(false);
    useEffect( ()=> {
        if (isEnrollingTransition) {
            return;
        }
        setCurrentStatus(status);
    }, [status, isEnrollingTransition]);
    
    const showAsEnrolled = currentStatus === ACTIVITY_TYPES.ACTIVE && !isEnrollingTransition;
    const hideEnrollmentChoiceCheckbox = isStateVA && currentStatus !== ACTIVITY_TYPES.ACTIVE;
    
    const validNumber = () => {

        if( (phoneNumber && phoneNumber.length === 10 && tempPhoneNumber === null ) || tempPhoneNumber && tempPhoneNumber.length === 10 ) {
            return true;
        } else {
            return false;
        }
    };

    const isValidated = validNumber() && (isChecked || hideEnrollmentChoiceCheckbox);
  
    const handleSubmit = async (e) => {
        e.preventDefault();
       
        if(!validNumber()) {
            setPhoneErrorMessage(true);
        }

        if (!isChecked && !hideEnrollmentChoiceCheckbox) {
            setErrorMessage(true);
        }

        if (!isValidated) {
            return;
        }

        const updateStatus =
            currentStatus === ACTIVITY_TYPES.ACTIVE ? ACTIVITY_TYPES.INACTIVE : ACTIVITY_TYPES.ACTIVE;
        setErrorMessage(false);

        if (updateStatus === ACTIVITY_TYPES.ACTIVE) {
            setIsEnrollingTransition(true);
        }

        const payload =
            tempPhoneNumber === null
                ? { ...brokerInfo, status: updateStatus }
                : { ...brokerInfo, status: updateStatus, phoneNumber: tempPhoneNumber };

        const result = await actions.updateBrokerConnectData(
            endpoints.BROKERCONNECT_PARTICIPATION_DETAILS,
            payload,
        );

        if (!result) {
            if (updateStatus === ACTIVITY_TYPES.ACTIVE) {
                setIsEnrollingTransition(false);
            }
            return;
        }

        setChecked(false);

        switch (updateStatus) {
            case ACTIVITY_TYPES.ACTIVE:
                navigate(routes.BROKER_CONNECT_AVAILABILITY, {
                    replace: true,
                    state: { redirectedFromParticipation: true },
                });
                break;
            case ACTIVITY_TYPES.INACTIVE:
                setSuccessMessage(true);
                break;
        }
    };

    const clearErrorMessage = () => {
        showPhoneErrorMessage ? setPhoneErrorMessage(false) : null;
    };

    const getLink = () => {
        return endpoints?.externalUrls?.brokerConnectParticipationAgreement?.[stateCode] ?? '#';
    };

    const brokerConnectParticipation = () => {
        if (showAsEnrolled) {
            return (<FormattedMessage id='brokerconnect.participation.msg.remove' />);
        }
        return (
            <FormattedMessage
                id='brokerconnect.participation.msg.agreement'
                values={{ link: <a href={getLink()} target='_blank' rel="noopener noreferrer" > <FormattedMessage id='brokerconnect.participation.msg.agreement_link' /></a> }}
            />
        );
    };
       
    return(
        <div className="participation-container">
            <div className="grid-container gi-panel" id="main-content">
                <ConditionalRendering isTrue={showSuccessMessage}>
                    <Alert
                        header="brokerconnect.participation.error_msg.successfully_unenrolled.header"
                        message="brokerconnect.participation.error_msg.successfully_unenrolled.message"
                        variation="success"
                    />
                </ConditionalRendering>
                <div className="header">
                    <h4 className="heading">
                        <FormattedMessage
                            id='brokerconnect.participation.header'
                        />
                    </h4>
                </div>
                <div className="padding-105">
                    <div className="grid-row">
                        <ConditionalRendering isTrue={showAsEnrolled}>
                            <p className="">
                                <FormattedMessage
                                    id='brokerconnect.participation.msg.currently_enrolled'
                                />
                            </p>
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={!showAsEnrolled}>
                            <div>
                                <p>
                                    <FormattedMessage
                                        id='brokerconnect.participation.msg.description'
                                        values={{ exchangeName }}
                                    />
                                </p>
                                <p>
                                    <FormattedMessage
                                        id='brokerconnect.participation.msg.provide_number'
                                    />
                                </p>
                                <div className="gi-component">
                                    <label className="gi-regular_input__label" htmlFor="brokerConnectPhone">
                                        <FormattedMessage
                                            id='brokerconnect.participation.msg.phone_number'
                                        />
                                    </label>
                                    <InputPhoneNumber id="brokerConnectPhone" { ...brokerInfo }  errorHandler={ clearErrorMessage } />
                                    <ConditionalRendering isTrue={showPhoneErrorMessage}>
                                        <ErrorMessage message='brokerconnect.error_msg.invalid_phonenumber' />
                                    </ConditionalRendering>
                                </div>
                                <p>
                                    <FormattedMessage
                                        id='brokerconnect.participation.msg.note_phone_number'
                                    />
                                </p>
                            </div>
                        </ConditionalRendering>
                    </div>
                    <form id="participation-form" className="" onSubmit={ handleSubmit }>
                        <div className="gi-component gi-checkbox">
                            <ConditionalRendering isTrue={!hideEnrollmentChoiceCheckbox}>
                                <input
                                    id="enrollmentChoiceCheckbox"
                                    className="usa-checkbox__input gi-checkbox__input"
                                    type="checkbox"
                                    onChange={() => setChecked(!isChecked)}
                                    checked={isChecked}
                                />
                                <label className="usa-checkbox__label gi-checkbox__label" htmlFor="enrollmentChoiceCheckbox">
                                    {brokerConnectParticipation()}
                                </label>
                            </ConditionalRendering>
                        </div>
                        <div className="">
                            <ConditionalRendering isTrue={showErrorMessage && !isChecked && !hideEnrollmentChoiceCheckbox}>
                                <ErrorMessage message="brokerconnect.participation.error_msg.checkbox_agree_to_terms" />
                            </ConditionalRendering>
                        </div>
                        <div className="controls-container">
                            <div className="grid-row controls">
                                <div className="grid-col-12 buttons-group txt-right">
                                    <button className="usa-button gi-button gi-button__primary" type="submit">
                                        <ConditionalRendering isTrue={showAsEnrolled}>
                                            <FormattedMessage id='brokerconnect.participation.remove_btn' />
                                        </ConditionalRendering>
                                        <ConditionalRendering isTrue={!showAsEnrolled}>
                                            <FormattedMessage id='brokerconnect.participation.confirm_btn' />
                                        </ConditionalRendering>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = state => ({
    brokerInfo: state.brokerConnect,
    environment: state.common.environment
});


const matchDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        updateBrokerConnectData
    }, dispatch)
});

export default connect(mapStateToProps, matchDispatchToProps)(Participation);